import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PoBreadcrumb, PoPageAction, PoDialogService, PoNotificationService } from '@po-ui/ng-components';
import { PendenciasService } from './pendencias.service';
import { TokenService } from '../../core/token/token.service';

@Component({
  selector: 'app-pendencias',
  templateUrl: './pendencias.component.html',
  styleUrls: ['./pendencias.component.css'],
  standalone: false
})
export class PendenciasComponent implements OnInit, OnDestroy {

  idEmpresa: number | null = null;
  isLoading: boolean = false;
  resposta: any = null;
  pdfUrl: SafeResourceUrl | null = null;
  private objectUrl: string | null = null;

  readonly breadcrumb: PoBreadcrumb = {
    items: [{ label: 'Home', link: '/home' }, { label: 'Relatórios' }, { label: 'Relatório de Pendências' }]
  };

  constructor(
    private readonly pendenciasService: PendenciasService,
    private readonly poDialogService: PoDialogService,
    private readonly poNotificationService: PoNotificationService,
    private readonly tokenService: TokenService,
    private readonly sanitizer: DomSanitizer
  ) { }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.revokeObjectUrl();
  }

  gerarRelatorio() {
    if (!this.tokenService.hasToken()) {
      this.poNotificationService.error('Você precisa estar autenticado para gerar o relatório. Por favor, faça login.');
      return;
    }

    const identificadorLimpo = this.idEmpresa != null ? String(this.idEmpresa) : '';

    if (!identificadorLimpo) {
      this.poNotificationService.error('Por favor, selecione uma empresa.');
      return;
    }


    this.isLoading = true;

    this.resposta = null;
    this.pdfUrl = null;
    this.revokeObjectUrl();
    
    this.pendenciasService.gerarRelatorioPendencias(identificadorLimpo).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.resposta = response;

        const resolvedPdfUrl = this.resolvePdfUrl(response);
        if (resolvedPdfUrl) {
          this.pdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(resolvedPdfUrl);
        } else {
          this.pdfUrl = null;
          this.poNotificationService.warning('O relatório foi gerado, mas não foi possível exibir o PDF.');
        }
        
        this.idEmpresa = null;
      },
      error: (error) => {
        this.isLoading = false;
        this.resposta = null;
        this.pdfUrl = null;
        console.error('Erro ao gerar relatório:', error);
        console.error('Status:', error.status);
        console.error('Mensagem:', error.message);
        console.error('Resposta completa:', error.error);
        
        // Tratamento específico para erro de conexão (status 0)
        if (error.status === 0) {
          this.poNotificationService.error('Não foi possível conectar ao servidor. Verifique se a API está rodando e se há problemas de CORS.');
          return;
        }
        
        // Tratamento específico para erro 500 (Internal Server Error)
        if (error.status === 500) {
          const errorMessage = error.error?.message || error.error?.title || 'Erro interno do servidor';
          console.error('Detalhes do erro 500:', {
            message: errorMessage,
            error: error.error,
            url: error.url
          });
          this.poNotificationService.error(`Erro no servidor: ${errorMessage}. Verifique os logs do backend.`);
          return;
        }
        
        if (error.error?.errors) {
          console.error('Erros de validação:', JSON.stringify(error.error.errors, null, 2));
         
        }
        
        if (error.error?.message) {
          this.poNotificationService.error(error.error.message);
        } else if (error.error?.errors) {
          this.poNotificationService.error('Erro de validação. Verifique os dados informados.');
        } else {
          this.poNotificationService.error('Erro ao gerar o relatório. Por favor, tente novamente.');
        }
      }
    });
  }

  private resolvePdfUrl(response: any): string | null {
    if (response == null) {
      return null;
    }

    // Resposta padrão do Ecco após GCS / cache: { pdfSignedUrl: "https://..." }
    const signed = response.pdfSignedUrl;
    if (typeof signed === 'string' && this.isUrl(signed)) {
      return signed;
    }

    // Caso 1: backend já retorna URL direta do PDF (signed URL / http)
    if (typeof response === 'string' && this.isUrl(response)) {
      return response;
    }

    // Caso 2: backend retorna objeto com campo "dados" em string JSON contendo "pdf" base64
    const dados = response?.dados;
    if (typeof dados === 'string') {
      try {
        const parsedDados = JSON.parse(dados);
        const pdfBase64 = parsedDados?.pdf;
        if (typeof pdfBase64 === 'string' && pdfBase64.length > 0) {
          return this.createObjectUrlFromBase64Pdf(pdfBase64);
        }
      } catch {
        // ignora parse e segue fallback
      }
    }

    // Caso 3: backend retorna objeto já com campo "pdf" base64
    if (typeof response?.pdf === 'string' && response.pdf.length > 0) {
      return this.createObjectUrlFromBase64Pdf(response.pdf);
    }

    return null;
  }

  private createObjectUrlFromBase64Pdf(base64: string): string {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });

    this.revokeObjectUrl();
    this.objectUrl = URL.createObjectURL(blob);
    return this.objectUrl;
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  private isUrl(value: string): boolean {
    return value.startsWith('http://') || value.startsWith('https://');
  }
}
